import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import StockTable, { StockTableSkeleton } from "../../interface/shared/stocktable";
import {
    useProductDetail,
    useProductExpiryWarnings,
    useProductStockLevelWarnings,
    ExpiryWarningLevels,
    StockLevelWarningLevels,
} from "../../api/products";
import { WeightDisplay } from "../../interface/shared/weightdisplay";
import { Product } from "../../shared/types";

export default function ProductDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {
        product,
        stock,
        errors,
        loading,
        deleting,
        fetchProduct,
        deleteProduct,
    } = useProductDetail(id);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const canDelete = product?.user != null;

    const handleConfirmDelete = async () => {
        const success = await deleteProduct();
        if (success) {
            setDeleteOpen(false);
            navigate("/products");
        }
    };

    return (
        <DashboardLayout>
            <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate("/products")}
                    >
                        Back
                    </Button>
                    {canDelete ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                            {product?.user ? (
                                <Box sx={{ textAlign: "right" }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Added by
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {product.user.name}
                                    </Typography>
                                    {product.user.email ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {product.user.email}
                                        </Typography>
                                    ) : null}
                                </Box>
                            ) : null}
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteRoundedIcon />}
                                onClick={() => setDeleteOpen(true)}
                                disabled={deleting}
                            >
                                Delete
                            </Button>
                        </Stack>
                    ) : null}
                </Stack>

                {errors.length > 0 && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchProduct}>
                                Retry
                            </Button>
                        }
                    >
                        {errors.join(" ")}
                    </Alert>
                )}

                {loading ? <_DetailsSkeleton /> : product && (
                    <StaggerContainer>
                        <StaggerItem>
                            <_ProductSummary product={product} stockCount={stock.length} />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Stock ({stock.length})
                            </Typography>

                            <StockTable stock={stock} emptyMessage="No stock found for this product." />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Expiry Warning Levels
                            </Typography>

                            <_ExpiryWarnings productId={id!} />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Stock Level Warning Levels
                            </Typography>

                            <_StockLevelWarnings productId={id!} />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Default Expiry Date
                            </Typography>

                            <_DefaultExpiryDate productId={id!} />
                        </StaggerItem>
                    </StaggerContainer>
                )}

                <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
                    <DialogTitle>Delete product</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this product? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                            onClick={handleConfirmDelete}
                        >
                            {deleting ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </DashboardLayout>
    );
}

function _ProductSummary({ product, stockCount }: { product: Product; stockCount: number }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">
                            {product.code} {product.main_code.length > 0 && product.main_code !== product.code ? `/ ${product.main_code}` : ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {product.name}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {product.user != null ? (
                            <Chip label="Warehouse only" size="small" variant="outlined" />
                        ) : null}
                        <Chip
                            label={product.active ? "Active" : "Inactive"}
                            size="small"
                            color={product.active ? "success" : "default"}
                        />
                        <Chip label={`${stockCount} stock entries`} size="small" />
                    </Stack>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Barcode</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.barcode || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Size</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.size || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Pack</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.pack || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Weight</Typography>
                        <Typography variant="body1" fontWeight={500}><WeightDisplay value={product.weight} /></Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">UOI</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.uoi ?? "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Price</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {product.price != null ? product.price.toFixed(2) : "—"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">VAT code</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.vat_code || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.status}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _ExpiryWarnings({ productId }: { productId: string }) {
    const { levels, loading, saving, error, success, setSuccess, saveLevels } = useProductExpiryWarnings(productId);
    const [draft, setDraft] = useState<ExpiryWarningLevels>({ notice: null, warning: null, critical: null });
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setDraft(levels);
        setDirty(false);
    }, [levels]);

    function handleChange(field: keyof ExpiryWarningLevels, value: string) {
        const parsed = value === "" ? null : parseInt(value, 10);
        if (value !== "" && isNaN(parsed as number)) return;
        setDraft((prev) => ({ ...prev, [field]: parsed }));
        setDirty(true);
    }

    if (loading) {
        return (
            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={3}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 12, sm: 4 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="rounded" height={40} sx={{ mt: 1 }} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    )}

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Notice (days)</Typography>
                            <TextField
                                placeholder="Notice (days)"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.notice ?? ""}
                                onChange={(e) => handleChange("notice", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Early warning threshold"
                                color="info"
                                focused={draft.notice != null}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Warning (days)</Typography>
                            <TextField
                                placeholder="Warning (days)"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.warning ?? ""}
                                onChange={(e) => handleChange("warning", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Approaching expiry threshold"
                                color="warning"
                                focused={draft.warning != null}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Critical (days)</Typography>
                            <TextField
                                placeholder="Critical (days)"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.critical ?? ""}
                                onChange={(e) => handleChange("critical", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Urgent expiry threshold"
                                color="error"
                                focused={draft.critical != null}
                            />
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={!dirty || saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
                            onClick={() => saveLevels(draft)}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            <Snackbar
                open={success}
                autoHideDuration={3000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={() => setSuccess(false)} variant="filled">
                    Warning levels saved successfully.
                </Alert>
            </Snackbar>
        </>
    );
}

function _DefaultExpiryDate({ productId }: { productId: string }) {

    const { product, loading, saving, errors, success, setSuccess, saveProduct } = useProductDetail(productId);
    const [draft, setDraft] = useState<number | null>(null);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setDirty(false);
        setDraft(product?.default_expiry ?? null);
    }, [product]);

    function handleChange(value: string) {
        const parsed = value === "" ? null : parseInt(value, 10);
        if (value !== "" && isNaN(parsed as number)) return;
        setDraft(parsed ?? null);
        setDirty(true);
    }

    if (loading) {
        return (
            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={3}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 12, sm: 4 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="rounded" height={40} sx={{ mt: 1 }} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <Box>
                    <Typography variant="body2">
                        Products which expire based on the production date can have the default shelf life set here. If you set this value it will automatically set the expiry date when a new stock entry is created via goods in.
                    </Typography>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary">Default Shelf Life (days)</Typography>
                        <TextField
                            placeholder="Default Shelf Life (days)"
                            type="number"
                            size="small"
                            fullWidth
                            value={draft ?? ""}
                            onChange={(e) => handleChange(e.target.value)}
                            slotProps={{ htmlInput: { min: 0 } }}
                            color="error"
                        />
                    </Grid>
                </Box>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={!dirty || saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
                        onClick={() => saveProduct({ ...product!, default_expiry: draft })}
                    >
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
}

function _StockLevelWarnings({ productId }: { productId: string }) {
    const { levels, loading, saving, error, success, setSuccess, saveLevels } = useProductStockLevelWarnings(productId);
    const [draft, setDraft] = useState<StockLevelWarningLevels>({ notice: null, warning: null, critical: null });
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setDraft(levels);
        setDirty(false);
    }, [levels]);

    function handleChange(field: keyof StockLevelWarningLevels, value: string) {
        const parsed = value === "" ? null : parseInt(value, 10);
        if (value !== "" && isNaN(parsed as number)) return;
        setDraft((prev) => ({ ...prev, [field]: parsed }));
        setDirty(true);
    }

    if (loading) {
        return (
            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={3}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 12, sm: 4 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="rounded" height={40} sx={{ mt: 1 }} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Notice level</Typography>
                            <TextField
                                placeholder="Notice level"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.notice ?? ""}
                                onChange={(e) => handleChange("notice", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Early low-stock threshold"
                                color="info"
                                focused={draft.notice != null}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Warning level</Typography>
                            <TextField
                                placeholder="Warning level"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.warning ?? ""}
                                onChange={(e) => handleChange("warning", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Low-stock threshold"
                                color="warning"
                                focused={draft.warning != null}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Critical level</Typography>
                            <TextField
                                placeholder="Critical level"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.critical ?? ""}
                                onChange={(e) => handleChange("critical", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Urgent low-stock threshold"
                                color="error"
                                focused={draft.critical != null}
                            />
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={!dirty || saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
                            onClick={() => saveLevels(draft)}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            <Snackbar
                open={success}
                autoHideDuration={3000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={() => setSuccess(false)} variant="filled">
                    Stock level thresholds saved successfully.
                </Alert>
            </Snackbar>
        </>
    );
}

function _DetailsSkeleton() {
    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 2 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={100} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={120} height={28} sx={{ mt: 4, mb: 2 }} />
            <StockTableSkeleton />
        </>
    );
}
